import React, { memo, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'store';
import { actionLanguagesInit, selectLanguagesStatuses } from 'store/languages';
import { selectLabelsStatuses } from 'store/labels';

/**
 * Boots the language bundle and gates the routed tree on it (F-13: option A of
 * docs/review/prompt-3/f13-recon.md).
 *
 * Until this gate the page under the route mounted in the same commit as this
 * provider, issued its data requests and painted with raw label keys; the
 * bundle then landed outside React and the memoised lists that had rendered in
 * the meantime never re-rendered (D-72). Rendering the children only once the
 * FIRST bundle has settled puts the page after the dictionary in the boot
 * order, which is the race's root.
 *
 * The gate reads the store, not a request: `labels.isInit` flips on fulfilled
 * and on rejected alike (an errored or aborted request included) and is never
 * reset, so a failed bundle releases the tree to the raw-key render it had
 * before this gate instead of a loader that never ends, and a later language
 * switch (isLoading true again, isInit still true) is not gated at all.
 * `languages.error` covers the boot where no labels request is ever issued
 * (the languages list failed or came back empty). While the gate holds, the
 * spinner LoadingOverlayProvider already shows for `languages.isLoading ||
 * labels.isLoading` is what the user sees: nothing of its own is rendered here.
 */
export const AppTranslateProvider = memo(({ children }: React.PropsWithChildren) => {
  const dispatch = useAppDispatch();
  const { isInit: isLabelsInit } = useAppSelector(selectLabelsStatuses);
  const { error: languagesError } = useAppSelector(selectLanguagesStatuses);

  useEffect(() => {
    dispatch(actionLanguagesInit());
  }, [dispatch]);

  if (!isLabelsInit && !languagesError) {
    return null;
  }

  return <>{children}</>;
});
